/**
 * Determines if the current device is a mobile device.
 * @param nav Navigator object, defaults to global navigator
 */
export function isMobileDevice(nav: Navigator = navigator): boolean {
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(nav.userAgent);
}

/**
 * Scrolls to the top left of the page on the first touch event.
 * @param options AddEventListenerOptions for `touchstart`, defaults to `{ once: true }`
 */
export function scrollOnTouch(options: AddEventListenerOptions = {once: true}): void {
  const handler = (): void => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    window.removeEventListener('touchstart', handler);
  };

  window.addEventListener('touchstart', handler, options);
}

/**
 * Executes scrollOnTouch only for mobile devices.
 * @param nav Navigator object (optional), defaults to global navigator
 * @param options AddEventListenerOptions, defaults to `{ once: true }`
 */
export function scrollOnTouchForMobileDevice(
  nav: Navigator = navigator,
  options: AddEventListenerOptions = {once: true}
): void {
  if (isMobileDevice(nav)) {
    scrollOnTouch(options);
  }
}

/**
 * iPhone, iPod, iPad, and iPadOS devices that report a desktop Macintosh user agent.
 * @param nav Navigator object, defaults to global navigator
 */
export function isIosDevice(nav: Navigator = navigator): boolean {
  if (/iPhone|iPad|iPod/i.test(nav.userAgent)) {
    return true;
  }
  return nav.platform === 'MacIntel' && nav.maxTouchPoints > 1;
}

/**
 * True while the on-screen keyboard has shrunk the visual viewport.
 * @param win Window to inspect, defaults to the global window
 */
export function isIosKeyboardOpen(win: Window = window): boolean {
  const viewport = win.visualViewport;
  if (!viewport) {
    return false;
  }
  return win.innerHeight - viewport.height > 80;
}

/**
 * Clears the scroll offset iOS leaves behind after the keyboard closes.
 * @param win Window to reset, defaults to the global window
 */
export function resetVisualViewport(win: Window = window): void {
  win.scrollTo(0, 0);
  win.document.documentElement.scrollTop = 0;
  win.document.body.scrollTop = 0;
}

/**
 * Blurs the focused field and waits until an open iOS keyboard has finished closing.
 * Other platforms, and iOS with the keyboard already closed, resolve immediately.
 * @param timeoutMs Maximum wait for the keyboard animation
 */
export function waitForIosKeyboardToClose(timeoutMs: number = 450): Promise<void> {
  if (!isIosDevice()) {
    return Promise.resolve();
  }
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    active.blur();
  }
  if (!isIosKeyboardOpen()) {
    return Promise.resolve();
  }
  const viewport = window.visualViewport;
  if (!viewport) {
    resetVisualViewport();
    return Promise.resolve();
  }
  const startHeight = viewport.height;
  return new Promise((resolve): void => {
    let settled = false;
    const finish = (): void => {
      if (settled) {
        return;
      }
      settled = true;
      viewport.removeEventListener('resize', onResize);
      resetVisualViewport();
      window.setTimeout((): void => {
        resolve();
      }, 40);
    };
    const onResize = (): void => {
      if (viewport.height > startHeight + 40) {
        window.setTimeout(finish, 40);
      }
    };
    viewport.addEventListener('resize', onResize);
    window.setTimeout(finish, timeoutMs);
  });
}
